import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AUDIT_CATEGORIES,
  AUDIT_CATEGORY_LABELS,
  auditEventDef,
  type AuditCategory,
  type AuditLogMeta,
  type OrgAuditEvent,
} from "@currencycore/shared";
import {
  exportAuditLog,
  getAuditLogMeta,
  getAuditLogs,
  type AuditFilters,
} from "../lib/api.ts";
import { toast } from "../components/Toast.tsx";

/**
 * Customer audit log (Business+). A filterable, paginated trail of account /
 * security / billing events for the org. Entitlement is gated server-side (and
 * the nav item is hidden for non-entitled orgs); this page also renders an
 * upgrade notice if reached directly without access.
 */

const TIME_RANGES: { label: string; ms: number | null }[] = [
  { label: "Last 24 hours", ms: 24 * 3600_000 },
  { label: "Last 7 days", ms: 7 * 24 * 3600_000 },
  { label: "Last 30 days", ms: 30 * 24 * 3600_000 },
  { label: "Last 90 days", ms: 90 * 24 * 3600_000 },
  { label: "All time", ms: null },
];

// A subtle accent per category for the event chip.
const CATEGORY_COLOR: Record<AuditCategory, string> = {
  api_key: "#6366f1",
  team: "#0ea5e9",
  billing: "#16a34a",
  security: "#dc2626",
  settings: "#9333ea",
  admin: "#d97706",
  api_activity: "#475569",
};

/** DB datetime ("YYYY-MM-DD HH:MM:SS", UTC) for `from`, matching the API format. */
function isoFloor(msAgo: number): string {
  return new Date(Date.now() - msAgo).toISOString().slice(0, 19).replace("T", " ");
}

function relativeTime(iso: string): string {
  // created_at is UTC without a zone marker — parse as UTC.
  const t = Date.parse(iso.replace(" ", "T") + "Z");
  if (Number.isNaN(t)) return iso;
  const secs = Math.round((Date.now() - t) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}

function absoluteTime(iso: string): string {
  const t = Date.parse(iso.replace(" ", "T") + "Z");
  return Number.isNaN(t) ? iso : new Date(t).toLocaleString();
}

function actorLabel(e: OrgAuditEvent): string {
  if (e.actorType === "admin") return "CurrencyCore support";
  if (e.actorType === "system") return e.actorName ?? "System";
  return e.actorName ?? "A team member";
}

function CategoryChip({ category }: { category: AuditCategory }) {
  const color = CATEGORY_COLOR[category];
  return (
    <span
      className="badge"
      style={{
        fontSize: "10px",
        color,
        background: `${color}1a`,
        border: `1px solid ${color}33`,
      }}
    >
      {AUDIT_CATEGORY_LABELS[category]}
    </span>
  );
}

export function AuditLogsPage() {
  const { orgId } = useParams<{ orgId: string }>();

  const [meta, setMeta] = useState<AuditLogMeta | null>(null);
  const [events, setEvents] = useState<OrgAuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Filters
  const [categories, setCategories] = useState<AuditCategory[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [actor, setActor] = useState("");
  const [rangeIdx, setRangeIdx] = useState(2); // default: last 30 days
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const filters = useMemo<AuditFilters>(() => {
    const range = TIME_RANGES[rangeIdx] ?? TIME_RANGES[2]!;
    return {
      categories: categories.length ? categories : undefined,
      events: selectedEvents.length ? selectedEvents : undefined,
      actor: actor || undefined,
      from: range.ms != null ? isoFloor(range.ms) : undefined,
      q: appliedSearch || undefined,
    };
  }, [categories, selectedEvents, actor, rangeIdx, appliedSearch]);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setForbidden(false);
    try {
      const page = await getAuditLogs(orgId, filters, null, 50);
      setEvents(page.events);
      setCursor(page.nextCursor);
    } catch (err) {
      if (err instanceof Error && /403/.test(err.message)) setForbidden(true);
      const status = (err as { status?: number })?.status;
      if (status === 403) setForbidden(true);
      setEvents([]);
      setCursor(null);
    } finally {
      setLoading(false);
    }
  }, [orgId, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  // Meta (actors + event list) loads once.
  useEffect(() => {
    if (!orgId) return;
    getAuditLogMeta(orgId)
      .then(setMeta)
      .catch(() => setMeta(null));
  }, [orgId]);

  async function loadMore() {
    if (!orgId || !cursor) return;
    setLoadingMore(true);
    try {
      const page = await getAuditLogs(orgId, filters, cursor, 50);
      setEvents((prev) => [...prev, ...page.events]);
      setCursor(page.nextCursor);
    } catch {
      toast("Couldn't load more events.");
    } finally {
      setLoadingMore(false);
    }
  }

  async function doExport(format: "csv" | "json") {
    if (!orgId) return;
    try {
      await exportAuditLog(orgId, filters, format);
    } catch {
      toast(`Couldn't export ${format.toUpperCase()}.`);
    }
  }

  function toggleCategory(c: AuditCategory) {
    setCategories((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }
  function toggleEvent(ev: string) {
    setSelectedEvents((prev) => (prev.includes(ev) ? prev.filter((x) => x !== ev) : [...prev, ev]));
  }
  function clearFilters() {
    setCategories([]);
    setSelectedEvents([]);
    setActor("");
    setRangeIdx(2);
    setSearch("");
    setAppliedSearch("");
  }

  const hasFilters =
    categories.length > 0 || selectedEvents.length > 0 || !!actor || !!appliedSearch || rangeIdx !== 2;

  // Events available to filter on, grouped by category (from meta).
  const eventsByCategory = useMemo(() => {
    const map = new Map<AuditCategory, string[]>();
    for (const ev of meta?.events ?? []) {
      const cat = auditEventDef(ev).category;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(ev);
    }
    return map;
  }, [meta]);

  if (forbidden) {
    return (
      <div className="animate-fade-in">
        <div className="page-head rise">
          <div>
            <h1>Audit log</h1>
            <p className="sub">A complete history of activity across your organization.</p>
          </div>
        </div>
        <div className="notice accent rise">
          <span className="ic">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
            </svg>
          </span>
          <span>
            The audit log is a <strong style={{ color: "var(--text)" }}>Business</strong> feature.
            Upgrade this organization to keep a durable, exportable trail of every key, billing,
            team, and security change.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="page-head rise" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <h1>Audit log</h1>
          <p className="sub">A complete history of activity across your organization.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-sm" type="button" onClick={() => void doExport("csv")}>Export CSV</button>
          <button className="btn btn-sm" type="button" onClick={() => void doExport("json")}>Export JSON</button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="card rise">
        <div className="card-pad" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Category chips */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {AUDIT_CATEGORIES.map((c) => {
              const on = categories.includes(c);
              const color = CATEGORY_COLOR[c];
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCategory(c)}
                  className="badge"
                  style={{
                    cursor: "pointer",
                    fontSize: "11px",
                    color: on ? "#fff" : color,
                    background: on ? color : `${color}14`,
                    border: `1px solid ${color}55`,
                  }}
                >
                  {AUDIT_CATEGORY_LABELS[c]}
                </button>
              );
            })}
          </div>

          {/* Row of selects + search */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <select className="input" style={{ maxWidth: 180 }} value={rangeIdx} onChange={(e) => setRangeIdx(Number(e.target.value))}>
              {TIME_RANGES.map((r, i) => (
                <option key={r.label} value={i}>{r.label}</option>
              ))}
            </select>

            <select className="input" style={{ maxWidth: 200 }} value={actor} onChange={(e) => setActor(e.target.value)}>
              <option value="">All actors</option>
              {(meta?.actors ?? []).map((a) => (
                <option key={a.id} value={a.id}>{a.name}{a.type === "admin" ? " (support)" : ""}</option>
              ))}
            </select>

            {/* Event multi-select dropdown */}
            <details className="audit-events-dd" style={{ position: "relative" }}>
              <summary className="btn btn-sm" style={{ listStyle: "none" }}>
                {selectedEvents.length ? `${selectedEvents.length} event${selectedEvents.length > 1 ? "s" : ""}` : "All events"}
              </summary>
              <div
                style={{
                  position: "absolute", zIndex: 30, marginTop: 6, minWidth: 240, maxHeight: 320, overflow: "auto",
                  background: "var(--surface, #fff)", border: "1px solid var(--border, #e5e7eb)", borderRadius: 10,
                  boxShadow: "0 12px 32px rgba(0,0,0,.12)", padding: 8,
                }}
              >
                {(meta?.events.length ?? 0) === 0 && <div className="muted" style={{ padding: 8, fontSize: 12 }}>No events yet.</div>}
                {AUDIT_CATEGORIES.filter((c) => eventsByCategory.has(c)).map((c) => (
                  <div key={c} style={{ marginBottom: 6 }}>
                    <div className="muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em", padding: "4px 6px" }}>
                      {AUDIT_CATEGORY_LABELS[c]}
                    </div>
                    {(eventsByCategory.get(c) ?? []).map((ev) => (
                      <label key={ev} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 6px", fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" checked={selectedEvents.includes(ev)} onChange={() => toggleEvent(ev)} />
                        {auditEventDef(ev).label}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </details>

            <form
              style={{ display: "flex", gap: 6, flex: 1, minWidth: 180 }}
              onSubmit={(e) => { e.preventDefault(); setAppliedSearch(search.trim()); }}
            >
              <input
                className="input"
                type="search"
                placeholder="Search target or details…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1 }}
              />
              <button className="btn btn-sm" type="submit">Search</button>
            </form>

            {hasFilters && (
              <button className="btn btn-sm btn-ghost" type="button" onClick={clearFilters}>Clear</button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card mt rise">
        {loading ? (
          <div className="card-pad">
            <div className="skeleton-page">
              {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: "2.6rem" }} />)}
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="card-pad" style={{ textAlign: "center", padding: "40px 20px" }}>
            <p className="muted">{hasFilters ? "No events match these filters." : "No activity recorded yet."}</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Time</th>
                  <th style={{ width: 170 }}>Actor</th>
                  <th>Event</th>
                  <th>Target</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const isOpen = expanded === e.id;
                  return (
                    <Fragment key={e.id}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : e.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td><span className="muted" title={absoluteTime(e.createdAt)} style={{ fontSize: 12 }}>{relativeTime(e.createdAt)}</span></td>
                        <td>
                          <span className="strong" style={{ fontSize: 13 }}>{actorLabel(e)}</span>
                        </td>
                        <td style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <CategoryChip category={e.category} />
                          <span style={{ fontSize: 13 }}>{e.eventLabel}</span>
                        </td>
                        <td><span className="muted mono" style={{ fontSize: 12 }}>{e.targetLabel ?? "—"}</span></td>
                        <td className="col-right">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s", opacity: .5 }}>
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={5} style={{ background: "var(--surface-2, #f8fafc)" }}>
                            <div style={{ padding: "10px 8px", display: "grid", gap: 6, fontSize: 12 }}>
                              <div><span className="muted">When:&nbsp;</span>{absoluteTime(e.createdAt)}</div>
                              {e.targetLabel && <div><span className="muted">Target:&nbsp;</span><span className="mono">{e.targetType ? `${e.targetType} · ` : ""}{e.targetLabel}</span></div>}
                              {e.ip && <div><span className="muted">IP:&nbsp;</span><span className="mono">{e.ip}</span></div>}
                              {e.detail && Object.keys(e.detail).length > 0 && (
                                <div>
                                  <span className="muted">Details:</span>
                                  <pre className="mono" style={{ margin: "4px 0 0", padding: 8, background: "var(--surface, #fff)", border: "1px solid var(--border, #e5e7eb)", borderRadius: 8, overflow: "auto" }}>
                                    {JSON.stringify(e.detail, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {cursor && !loading && (
          <div className="card-pad" style={{ textAlign: "center" }}>
            <button className="btn btn-sm" type="button" onClick={() => void loadMore()} disabled={loadingMore}>
              {loadingMore ? <><span className="spinner" />Loading…</> : "Load more"}
            </button>
          </div>
        )}
      </div>

      {meta?.retentionDays ? (
        <p className="muted" style={{ fontSize: 12, marginTop: 12, textAlign: "center" }}>
          Events are retained for {Math.round(meta.retentionDays / 30)} months.
        </p>
      ) : null}
    </div>
  );
}
